/* eslint-disable react/require-default-props */
import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { useImpression } from '@thd-olt-component-react/impression';
import propTypes from 'prop-types';

const {
  bool, func, number, oneOf, shape, string
} = propTypes;

export const MiniSwatch = ({
  swatchGuid,
  value,
  isSelected,
  enableHover,
  onClick,
  onMouseEnter,
  onMouseLeave,
  swatchItemId = null,
  index = 0,
  componentImpressionData = {
    name: '',
    component: '',
    position: 1,
    type: '',
    anchorSku: ''
  },
  isImpressionsRequired = false,
  wrapperClasses = null,
  swatchSize = '6',
  ariaLabel = '',
  swatchName = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClickedMobile, setIsClickedMobile] = useState(false);

  const mouseEnter = useCallback((event) => {
    if (enableHover) {
      setIsHovered(true);
      if (typeof onMouseEnter === 'function') {
        onMouseEnter(event);
      }
    }
  }, [enableHover, onMouseEnter]);

  const mouseLeave = useCallback((event) => {
    if (enableHover) {
      setIsHovered(false);
      if (typeof onMouseLeave === 'function') {
        onMouseLeave(event);
      }
    }
  }, [enableHover, onMouseLeave]);

  const onClickMobile = () => {
    if (!enableHover) {
      setIsClickedMobile(true);
    }
  };

  const impressionProviderContextData = useImpression({
    data: {
      id: swatchItemId || '',
      name: componentImpressionData?.name || '',
      component: componentImpressionData?.component || '',
      position: componentImpressionData?.position || 1,
      type: componentImpressionData?.type || '',
      category: 'ssku',
      anchorSku: componentImpressionData?.anchorSku || ''
    }
  });

  if (!swatchGuid) {
    return null;
  }

  // Safety check: Ensure impressionProviderContextData exists
  const impressionData = impressionProviderContextData || { ref: null, clickID: null };
  const shouldUseImpressionData = index !== 0
    && isImpressionsRequired
    && (isHovered || isClickedMobile)
    && Boolean(impressionData?.ref);

  // Always use the same ref to prevent DOM manipulation issues
  const swatchRef = shouldUseImpressionData ? impressionData.ref : null;
  const swatchClickId = shouldUseImpressionData ? impressionData.clickID : null;

  const validSwatchSize = ['4', '5', '6', '7', '8', '9'].includes(swatchSize) ? swatchSize : '6';

  const swatchCardClasses = classNames(
    `sui-w-${validSwatchSize} sui-h-${validSwatchSize} `,
    'sui-rounded-md sui-border-solid sui-border-1 sui-border-[#fff] sui-bg-transparent',
    {
      'sui-outline-2 sui-outline sui-outline-stronger hover:sui-bg-subtle focus-visible:sui-bg-subtle':
      isSelected
    },
    {
      'hover:sui-outline hover:sui-outline-accent hover:sui-outline-2 sui-outline sui-outline-primary sui-outline-1':
      !isSelected
    }
  );

  return (
    <div
      data-testid="mini-swatch-wrapper"
      className={wrapperClasses}
      key={`mini-swatch-${swatchName}-${index}`}
    >
      <button
        type="button"
        data-testid="mini-swatch-body-card"
        data-clickid={swatchClickId || undefined}
        onMouseEnter={mouseEnter}
        onMouseLeave={mouseLeave}
        onClick={(event) => {
          onClickMobile();
          if (typeof onClick === 'function') {
            onClick(event);
          }
        }}
        className={swatchCardClasses}
        value={value || ''}
        aria-pressed={isSelected}
        aria-label={
          ariaLabel || `${swatchName || 'Swatch'} ${isSelected ? 'selected' : 'not selected'}`
        }
        ref={swatchRef}
      >
        <div className="sui-w-full sui-h-full sui-rounded-md sui-bg-transparent sui-overflow-hidden">
          <img
            alt={`${swatchName || 'Swatch'} color or pattern variation`}
            src={swatchGuid}
            width="100%"
            height="100%"
            className="sui-w-full sui-h-full sui-object-cover sui-rounded-md"
          />
        </div>
      </button>
    </div>
  );
};

MiniSwatch.displayName = 'MiniSwatch';

MiniSwatch.propTypes = {
  swatchGuid: string.isRequired,
  value: string.isRequired,
  isSelected: bool.isRequired,
  enableHover: bool.isRequired,
  onClick: func.isRequired,
  onMouseEnter: func.isRequired,
  onMouseLeave: func.isRequired,
  swatchItemId: string,
  index: number,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
    anchorSku: string
  }),
  isImpressionsRequired: bool,
  wrapperClasses: string,
  /** Default '6'; See Stencil Design System 'Spacing Tokens' for available pixel values */
  swatchSize: oneOf(['4', '5', '6', '7', '8', '9']),
  ariaLabel: string,
  swatchName: string
};
